import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bell, Settings, Receipt, CheckCircle, XCircle, Mail, UserPlus,
  HardHat, Euro, AlertTriangle, Calendar, CheckSquare, MessageSquare,
  Moon, Mail as MailIcon, Smartphone, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

const ICON_MAP = {
  Receipt: Receipt,
  CheckCircle: CheckCircle,
  XCircle: XCircle,
  Mail: Mail,
  UserPlus: UserPlus,
  HardHat: HardHat,
  Euro: Euro,
  AlertTriangle: AlertTriangle,
  Calendar: Calendar,
  CheckSquare: CheckSquare,
  MessageSquare: MessageSquare,
  Bell: Bell
};

const COLOR_MAP = {
  purple: 'bg-purple-100 text-purple-600',
  green: 'bg-green-100 text-green-600',
  red: 'bg-red-100 text-red-600',
  orange: 'bg-orange-100 text-orange-600',
  blue: 'bg-blue-100 text-blue-600',
  amber: 'bg-amber-100 text-amber-600'
};

export default function NotificationSettingsPage() {
  const { user } = useOutletContext();
  const [preferences, setPreferences] = useState(null);
  const [notificationTypes, setNotificationTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const [prefsResponse, typesResponse] = await Promise.all([
        fetch(`${API_URL}/api/notifications/preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/notifications/types`)
      ]);

      if (prefsResponse.ok) {
        setPreferences(await prefsResponse.json());
      }
      if (typesResponse.ok) {
        setNotificationTypes(await typesResponse.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (key, value) => {
    const token = localStorage.getItem('client_token');
    
    // Optimistic update
    setPreferences(prev => ({ ...prev, [key]: value }));

    try {
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [key]: value })
      });

      if (!response.ok) {
        throw new Error('Failed to update');
      }
    } catch (error) {
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !value }));
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const ToggleSwitch = ({ enabled, onChange, disabled = false }) => (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        enabled ? 'bg-rm-primary' : 'bg-slate-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  const notificationCategories = [
    {
      title: "Assistant & Workflow",
      description: "Notifications liées à ton assistant",
      items: [
        { key: 'invoice_approval', type: 'invoice_approval' },
        { key: 'urgent_request', type: 'urgent_request' },
        { key: 'task_assigned', type: 'task_assigned' }
      ]
    },
    {
      title: "Commercial",
      description: "Devis, prospects et paiements",
      items: [
        { key: 'quote_accepted', type: 'quote_accepted' },
        { key: 'quote_refused', type: 'quote_refused' },
        { key: 'new_prospect', type: 'new_prospect' },
        { key: 'payment_received', type: 'payment_received' }
      ]
    },
    {
      title: "Chantiers & SAV",
      description: "Suivi de tes chantiers et tickets",
      items: [
        { key: 'chantier_completed', type: 'chantier_completed' },
        { key: 'ticket_urgent', type: 'ticket_urgent' }
      ]
    },
    {
      title: "Communication",
      description: "Messages et emails",
      items: [
        { key: 'urgent_email', type: 'urgent_email' },
        { key: 'message_received', type: 'message_received' },
        { key: 'appointment_reminder', type: 'appointment_reminder' }
      ]
    }
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notification-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-rm-text flex items-center gap-2">
          <Bell className="w-6 h-6 text-rm-primary" />
          Préférences de notifications
        </h1>
        <p className="text-rm-text-secondary mt-1">
          Choisis quand et comment tu veux être notifié
        </p>
      </div>

      {/* General Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
      >
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <h2 className="font-semibold text-rm-text flex items-center gap-2">
            <Settings className="w-5 h-5 text-rm-primary" />
            Paramètres généraux
          </h2>
        </div>
        <div className="divide-y divide-slate-100">
          {/* Email notifications */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MailIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-rm-text">Notifications par email</p>
                <p className="text-sm text-slate-500">Recevoir les alertes importantes par email</p>
              </div>
            </div>
            <ToggleSwitch
              enabled={preferences?.email_notifications}
              onChange={(v) => updatePreference('email_notifications', v)}
            />
          </div>

          {/* Push notifications */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-rm-text">Notifications in-app</p>
                <p className="text-sm text-slate-500">Voir les notifications dans l'application</p>
              </div>
            </div>
            <ToggleSwitch
              enabled={preferences?.push_notifications}
              onChange={(v) => updatePreference('push_notifications', v)}
            />
          </div>

          {/* Quiet hours */}
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Moon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-rm-text">Mode silencieux</p>
                  <p className="text-sm text-slate-500">Pas de notifications pendant ces heures</p>
                </div>
              </div>
              <ToggleSwitch
                enabled={preferences?.quiet_hours_enabled}
                onChange={(v) => updatePreference('quiet_hours_enabled', v)}
              />
            </div>
            {preferences?.quiet_hours_enabled && (
              <div className="mt-3 ml-13 flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">De</span>
                <input
                  type="time"
                  value={preferences?.quiet_hours_start || '22:00'}
                  onChange={(e) => updatePreference('quiet_hours_start', e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded-lg text-sm"
                />
                <span className="text-slate-600">à</span>
                <input
                  type="time"
                  value={preferences?.quiet_hours_end || '08:00'}
                  onChange={(e) => updatePreference('quiet_hours_end', e.target.value)}
                  className="px-2 py-1 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            )}
          </div>

          {/* Daily digest */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-rm-text">Résumé quotidien</p>
                <p className="text-sm text-slate-500">Recevoir un email récapitulatif chaque matin</p>
              </div>
            </div>
            <ToggleSwitch
              enabled={preferences?.daily_digest}
              onChange={(v) => updatePreference('daily_digest', v)}
            />
          </div>
        </div>
      </motion.div>

      {/* Notification Types by Category */}
      {notificationCategories.map((category, catIndex) => (
        <motion.div
          key={category.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: catIndex * 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
        >
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <h2 className="font-semibold text-rm-text">{category.title}</h2>
            <p className="text-sm text-slate-500">{category.description}</p>
          </div>
          <div className="divide-y divide-slate-100">
            {category.items.map((item) => {
              const typeInfo = notificationTypes[item.type];
              if (!typeInfo) return null;
              
              const Icon = ICON_MAP[typeInfo.icon] || Bell;
              const colorClass = COLOR_MAP[typeInfo.color] || COLOR_MAP.blue;

              return (
                <div key={item.key} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-rm-text">{typeInfo.label}</p>
                      <p className="text-sm text-slate-500">{typeInfo.description}</p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={preferences?.[item.key]}
                    onChange={(v) => updatePreference(item.key, v)}
                  />
                </div>
              );
            })}
          </div>
        </motion.div>
      ))}

      {/* Info Card */}
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-blue-900">Bon à savoir</p>
            <p className="text-sm text-blue-700 mt-1">
              Les notifications urgentes de ton assistant (🚨) seront toujours affichées, 
              même si tu as désactivé certaines catégories. Tu peux les désactiver spécifiquement 
              dans "Demande urgente" ci-dessus si nécessaire.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
